import { gql } from "@apollo/client";

export const UPDATE_USER_BY_ID = gql`
  mutation updateUserById($userId: Int, $updateUser: UpdateUser) {
    updateUserById(userId: $userId, updateUser: $updateUser) {
      name
    }
  }
`;

export const DELETE_USER_BY_ID = gql`
  mutation deleteUserById($userId: Int) {
    deleteUserById(userId: $userId) {
      id
    }
  }
`;

export const UPDATE_USER_CUSTOM_PRICING = gql`
  mutation updateUserCustomPricing(
    $userId: Int
    $customPricing: CustomPricingInput
  ) {
    updateUserCustomPricing(userId: $userId, customPricing: $customPricing) {
      GOOGLE_STORE {
        id
        DIRECT
        VISIT
        REVIEWS
        RATINGS
        KEYWORDS
        KEYWORDS_PRO
      }
      APP_STORE {
        id
        DIRECT
        VISIT
        REVIEWS
        RATINGS
        KEYWORDS
        KEYWORDS_PRO
      }
    }
  }
`;

// The contractor's prices per unit in yuan; super admin only.
export const GET_BUYER_PRICING = gql`
  query getBuyerPricing {
    getBuyerPricing {
      APP_STORE {
        DIRECT
        KEYWORDS
        KEYWORDS_PRO
        RATINGS
        REVIEWS
      }
      GOOGLE_STORE {
        DIRECT
        KEYWORDS
        KEYWORDS_PRO
        RATINGS
        REVIEWS
      }
    }
  }
`;

export const UPDATE_BUYER_PRICING = gql`
  mutation updateBuyerPricing($pricing: BuyerPricingInput!) {
    updateBuyerPricing(pricing: $pricing) {
      APP_STORE {
        DIRECT
      }
    }
  }
`;

export const UPDATE_DEFAULT_PRICING = gql`
  mutation updateDefaultPricing(
    $profileLevel: ProfileLevels
    $customPricing: CustomPricingInput
  ) {
    updateDefaultPricing(
      profileLevel: $profileLevel
      customPricing: $customPricing
    ) {
      GOOGLE_STORE {
        id
        DIRECT
        VISIT
        REVIEWS
        RATINGS
        KEYWORDS
        KEYWORDS_PRO
      }
      APP_STORE {
        id
        DIRECT
        VISIT
        REVIEWS
        RATINGS
        KEYWORDS
        KEYWORDS_PRO
      }
    }
  }
`;

export const GET_DEFAULT_PRICES = gql`
  query getDefaultPricing {
    getDefaultPricing {
      STARTER {
        GOOGLE_STORE {
          id
          REVIEWS
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
          DIRECT
          VISIT
        }
        APP_STORE {
          id
          REVIEWS
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
          DIRECT
          VISIT
        }
      }
      ADVANCED {
        GOOGLE_STORE {
          id
          REVIEWS
          DIRECT
          VISIT
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
        APP_STORE {
          id
          REVIEWS
          DIRECT
          VISIT
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
      }
      EXPERT {
        GOOGLE_STORE {
          id
          REVIEWS
          DIRECT
          VISIT
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
        APP_STORE {
          id
          REVIEWS
          DIRECT
          VISIT
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
      }
      # CUSTOM {
      #   GOOGLE_STORE {
      #     id
      #     REVIEWS
      #     RATINGS
      #     KEYWORDS
      #   }
      #   APP_STORE {
      #     id
      #     REVIEWS
      #     RATINGS
      #     KEYWORDS
      #   }
      # }
    }
  }
`;

export const GET_DEFAULT_PRICES_WITH_LIMITS = gql`
  query getDefaultPricingWithLimits {
    getDefaultPricing {
      STARTER {
        LIMITS {
          apps
          keywords
        }
        GOOGLE_STORE {
          id
          REVIEWS
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
          DIRECT
          VISIT
        }
        APP_STORE {
          id
          REVIEWS
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
          DIRECT
          VISIT
        }
      }
      ADVANCED {
        LIMITS {
          apps
          keywords
        }
        GOOGLE_STORE {
          id
          REVIEWS
          DIRECT
          VISIT
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
        APP_STORE {
          id
          REVIEWS
          DIRECT
          VISIT
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
      }
      EXPERT {
        LIMITS {
          apps
          keywords
        }
        GOOGLE_STORE {
          id
          REVIEWS
          DIRECT
          VISIT
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
        APP_STORE {
          id
          REVIEWS
          DIRECT
          VISIT
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
      }
    }
  }
`;

export const CREATE_USER = gql`
  mutation createUser($user: UserInput) {
    createUser(user: $user) {
      name
    }
  }
`;

export const CHANGE_USER_STATUS = gql`
  mutation changeUserStatus($userId: Int, $userStatus: ProfileLevels) {
    changeUserStatus(userId: $userId, userStatus: $userStatus) {
      id
    }
  }
`;

export const ADD_BALANCE = gql`
  mutation addBalance(
    $userId: Int
    $paymentOption: PaymentOption
    $sum: Float
  ) {
    addBalance(userId: $userId, paymentOption: $paymentOption, sum: $sum) {
      id
    }
  }
`;

export const REMOVE_BALANCE = gql`
  mutation removeBalance(
    $userId: Int
    $paymentOption: PaymentOption
    $sum: Float
  ) {
    removeBalance(userId: $userId, paymentOption: $paymentOption, sum: $sum) {
      id
    }
  }
`;

// The admins' note on a client; only the admin queries return it.
const USER_ADMIN_COMMENT_FIELDS = gql`
  fragment UserAdminCommentFields on UserAdminComment {
    comment
    updatedAt
    updatedBy {
      id
      name
      email
    }
  }
`;

export const GET_ALL_USERS = gql`
  query getAllUsers {
    getAllUsers {
      id
      name
      email
      totalSpendings
      timezone
      balance
      profileLevel
      discount
      referallLink
      allTimeBonuses
      activeBonuses
      organization
      phone
      telegram
      lastActivity
      createdAt
      updatedAt
      manager {
        id
        name
        email
      }
      adminComment {
        ...UserAdminCommentFields
      }
      connectedApps {
        size
        updated
        title
        appId
        store
        country
        score
        version
        status
      }
      userOrders {
        orderType
        orderId
      }
      payments {
        cardType
        depositAmount
        depositDate
        paymentOption
        internalId
        bankName
        accountHolderName
        accountNumber
        routingNumber
        paymentEmail
      }
      userPricing {
        GOOGLE_STORE {
          id
          DIRECT
          VISIT
          REVIEWS
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
        APP_STORE {
          id
          DIRECT
          VISIT
          REVIEWS
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
      }
    }
  }
  ${USER_ADMIN_COMMENT_FIELDS}
`;

export const GET_ALL_ADMINS = gql`
  query getAllAdmins {
    getAllAdmins {
      id
      name
      email
      role
      position
    }
  }
`;

export const SET_USER_MANAGER = gql`
  mutation setUserManager($userId: Int!, $managerId: Int) {
    setUserManager(userId: $userId, managerId: $managerId) {
      id
      manager {
        id
        name
        email
      }
    }
  }
`;

// A blank comment removes it.
export const SET_USER_ADMIN_COMMENT = gql`
  mutation setUserAdminComment($userId: Int!, $comment: String) {
    setUserAdminComment(userId: $userId, comment: $comment) {
      id
      adminComment {
        ...UserAdminCommentFields
      }
    }
  }
  ${USER_ADMIN_COMMENT_FIELDS}
`;

export const GET_USER_ROLE = gql`
  query getUser {
    user {
      role
    }
  }
`;

export const ADD_ADMIN = gql`
  mutation addAdmin($email: String) {
    addAdmin(email: $email) {
      id
    }
  }
`;

export const REMOVE_ADMIN = gql`
  mutation removeAdmin($email: String) {
    removeAdmin(email: $email) {
      id
    }
  }
`;

export const GET_USER = gql`
  query getUser {
    user {
      role
      name
      email
    }
  }
`;

export const GET_USER_INVOICES_BY_ID = gql`
  query getUserInvoicesById($userId: Int){
    getUserInvoicesById(userId: $userId){
      id
      paymentEmail
      accountEmail
      bankName
      accountHolderName
      accountNumber
      routingNumber
      amount
    }
  }
`

export const GET_USER_BY_ID = gql`
  query getUserById($userId: Int) {
    getUserById(userId: $userId) {
      id
      name
      email
      totalSpendings
      timezone
      balance
      profileLevel
      discount
      referallLink
      allTimeBonuses
      activeBonuses
      organization
      phone
      telegram
      lastActivity
      keywordLimit
      applicationLimit
      connectedApps {
        size
        updated
        title
        appId
        store
        country
        score
        version
        status
      }
      userOrders {
        orderType
        orderId
      }
      payments {
        cardType
        depositAmount
        depositDate
        paymentOption
        internalId
        bankName
        accountHolderName
        accountNumber
        routingNumber
        paymentEmail
      }
      userPricing {
        GOOGLE_STORE {
          id
          DIRECT
          VISIT
          REVIEWS
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
        APP_STORE {
          id
          DIRECT
          VISIT
          REVIEWS
          RATINGS
          KEYWORDS
          KEYWORDS_PRO
        }
      }
    }
  }
`;

// The user's apps as their My Apps shows them. Asked apart from the user, so
// the page still opens against a backend that has no such query.
export const GET_USER_APPS = gql`
  query getUserApps($userId: Int!) {
    getUserApps(userId: $userId) {
      appId
      title
      icon
      store
      country
      language
      status
      score
      reviewsCount
      version
      updated
      size
      keywordsCount
      totalSpend
      activeOrdersCount
    }
  }
`;
